import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import i18n, { LocalKeys, useTranslation } from "utilities/i18n";
import { DOCS } from "Constants";
import { HelpLink, SubmitButton, ToolTip } from "elements";
import { OptionType } from "elements/DropdownList";
import { SelectProps } from "collections/Form/Select";
import { Link, useQueryParamString } from "polyrouter";
import { positiveNumberFilter } from "../Rooms/Create/CreateRoomModal";
import {
  StandardFormSelect,
  Form,
  Formik,
  Input,
  FormikConfig,
  Group,
  useFormikContext,
  AddressInput
} from "collections/Form";
import { Card } from "collections/Cards";
import { DISMISS, useNotification } from "collections/Notifications";
import {
  RegisterDeviceMutation,
  Site,
  useRegisterDeviceMutation,
  useTenantsListQuery
} from "generated/graphql";
import { Layout } from "views/Main/Layouts";
import "./RegisterDevice.scss";
import useSitesAndRooms from "./useSitesAndRooms";
import * as yup from "yup";
import { InferType } from "yup";
import { generatePath } from "react-router";
import { isString } from "lodash";
import { isMobile } from "react-device-detect";
import { nameIsUnassigned } from "utilities/stringUtils";

const field = (label: LocalKeys) => {
  return yup
    .string()
    .label(i18n.t(label))
    .required()
    .default("");
};

/**
 * AUTHORITATIVE INFORMATION ON REGISTRATION PINS:
 *
 * 6 characters, uppercase: left out 0, Q and O to avoid issues in differentiating
 * "They're always 6 characters and the alphabet used is
 * 123456789ABCDEFGHIJKLMNPRSTUVWXYZ
 * The code generator itself is "NanoID" and uses
 * a "NanoID-good" package to make sure we don't get
 * any undesired words by accident.
 */
export const RegisterDeviceSchema = yup.object({
  tenantId: field("TENANT_ID").required(),
  registrationCode: field("DEVICE_PIN")
    .required(i18n.t("FORM_VALIDATE_PIN_CODE"))
    .length(6)
    .matches(
      /[123456789ABCDEFGHIJKLMNPRSTUVWXYZ]{6}/i,
      i18n.t("FORM_VALIDATE_PIN_CODE")
    ),
  deviceName: field("DEVICE_NAME"),
  roomName: field("ROOM_NAME"),
  floorName: field("FLOOR_NAME").notRequired(),
  capacity: field("CAPACITY").notRequired(),
  siteName: field("SITE_NAME")
});
export const SiteAddressSchema = yup.object({
  siteAddress: yup.object({
    address1: field("ADDRESS").required(i18n.t("SELECT_ADDRESS_ERROR")),
    geohash: field("GEOHASH").notRequired()
  }),
  extras: yup.object({
    fromLookup: yup
      .boolean()
      .notRequired()
      .default(false)
  })
});
export type SiteAddressSchemaType = InferType<typeof SiteAddressSchema>;
export const RegisterFormSchema = RegisterDeviceSchema.concat<
  SiteAddressSchemaType
>(SiteAddressSchema);
export type SiteAddress = InferType<typeof SiteAddressSchema>;
export type RegisterFormType = InferType<typeof RegisterFormSchema>;
const initialState: RegisterFormType = RegisterFormSchema.default();

export const extractSiteData = (site?: Partial<Site>) => {
  if (site) {
    if (site.address) {
      const { geohash, formattedAddress } = site.address;
      return {
        address1: formattedAddress || "",
        geohash: geohash || ""
      };
    }
  }
  return null;
};

/**
 * A side effect component, exsists entirely to set the address fields when you change an address lookup field
 * that is tied to a site.
 */
type SyncSiteAddressProps<T extends SiteAddress> = {
  siteNameKey: keyof T;
  initialValues: T;
  setSiteAddressDisabled?: Dispatch<SetStateAction<boolean>>;
};

export function SyncSiteAddress<T extends SiteAddress>({
  siteNameKey,
  setSiteAddressDisabled
}: SyncSiteAddressProps<T>) {
  const { roomsRef, siteMeta } = useSitesAndRooms();
  const { values, setValues } = useFormikContext<T>();

  const siteNameFromValues = (v: T) => {
    const value = v[siteNameKey];
    return isString(value) ? value : "";
  };

  let siteNameOrId = siteNameFromValues(values);
  const [oldName, updateName] = useState("");
  const siteNameUpdated = oldName !== siteNameOrId;
  useEffect(() => {
    if (siteNameUpdated && siteMeta && siteNameOrId) {
      const siteById: any = siteMeta.sites.find(
        site => site.id === siteNameOrId
      );

      let newAddress = extractSiteData(siteById) ?? values.siteAddress;
      if (!siteById) {
        const siteByName: any = siteMeta.sites.find(
          site => site.name === siteNameOrId
        );
        newAddress = extractSiteData(siteByName) ?? {
          address1: "",
          geohash: ""
        };
      }

      if (typeof setSiteAddressDisabled === "function") {
        const disabled =
          !!newAddress.address1 || nameIsUnassigned(siteNameOrId);
        setSiteAddressDisabled(disabled);
      }

      setValues({
        ...values,
        roomName: "",
        floorName: "",
        capacity: "",
        siteAddress: newAddress
      });
      updateName(siteNameOrId);
    } else if (siteNameUpdated && siteNameOrId === "") {
      setValues({
        ...values,
        roomName: "",
        floorName: "",
        capacity: "",
        siteAddress: ""
      });
      updateName("");
    }
  }, [
    updateName,
    roomsRef,
    setValues,
    siteMeta,
    siteNameOrId,
    siteNameUpdated,
    values,
    setSiteAddressDisabled
  ]);
  return <></>;
}

const SyncTenant: React.FC = () => {
  const { values, setValues } = useFormikContext<RegisterFormType>();
  const [tid] = useQueryParamString("tenantId");
  const queryTenantId = tid ?? "";
  useEffect(() => {
    if (queryTenantId !== values.tenantId) {
      setValues({
        ...values,
        tenantId: queryTenantId,
        deviceName: "",
        siteName: "",
        roomName: "",
        floorName: "",
        capacity: ""
      });
    }
  }, [values, setValues, queryTenantId]);
  return null;
};

const Tenants: React.FC = () => {
  const { t } = useTranslation();
  const { data: tenantData } = useTenantsListQuery();
  const tenants = (tenantData?.tenants ?? [])
    .map(
      tenant =>
        ({
          value: tenant.id,
          text: tenant.name || t("UNKNOWN_TENANT")
        } as OptionType)
    )
    .sort((a: OptionType, b: OptionType) => {
      return a.text.localeCompare(b.text, undefined, { sensitivity: "base" });
    });
  const { values, setValues } = useFormikContext<RegisterFormType>();
  const [, setQueryTenantId] = useQueryParamString("tenantId");
  return (
    <StandardFormSelect
      label={t("ACCOUNT")}
      name="tenantId"
      options={tenants}
      onSelectionChange={val => {
        setValues({
          ...values,
          deviceName: "",
          siteName: "",
          roomName: "",
          floorName: "",
          capacity: ""
        });
        setQueryTenantId(val);
      }}
    />
  );
};

const Room: React.FC<SelectProps & { label: string }> = props => {
  const { values, setValues } = useFormikContext<RegisterFormType>();
  const { siteMeta } = useSitesAndRooms();
  const [priorRoomName, setRoom] = useState("");

  useEffect(() => {
    if (values.roomName !== priorRoomName) {
      const site = values.siteName
        ? siteMeta?.sites.find(site => site.name === values.siteName)
        : undefined;
      const room = values.roomName
        ? site?.rooms.find(room => room.name === values.roomName)
        : undefined;
      setValues({
        ...values,
        floorName: room?.floor ?? "",
        capacity: room?.capacity?.toString() ?? ""
      });
      setRoom(values.roomName);
    }
  }, [priorRoomName, values, setValues, siteMeta, setRoom]);
  return <StandardFormSelect {...props} customValue search selectOnInput />;
};

const Help = () => {
  const { t } = useTranslation();
  return (
    <div className="help">
      <span>{t("DEVICE_PIN_HELP")}</span>
      {"   "}
      <ToolTip
        icon="help_circle"
        iconPosition="right"
        position="bottom"
        title={t("DEVICE_PIN_TOOLTIP_TITLE")}
        iconTitle="help"
      >
        <p>{t("DEVICE_PIN_TOOLTIP_MESSAGE")}</p>
        <HelpLink location={DOCS.PIN_ONBOARDING}>
          {t("READ_THE_DOCUMENTATION")}
        </HelpLink>
      </ToolTip>
    </div>
  );
};

const RegisterDeviceForm: React.FC<{
  onSubmit: FormikConfig<RegisterFormType>["onSubmit"];
  initialPin: string;
}> = ({ onSubmit, initialPin }) => {
  const { t } = useTranslation();
  let { sitesWithNames: sites, siteMeta } = useSitesAndRooms();

  const [siteAddressDisabled, setSiteAddressDisabled] = useState(false);

  const initialStateWithParams = {
    ...initialState,
    floorName: initialState.floorName || "",
    capacity: initialState.capacity?.toString() || "",
    registrationCode: initialPin || "",
    tenantId: ""
  };

  sites = sites.filter(s => !nameIsUnassigned(s.text));

  let roomsForSite: Array<OptionType> = [];

  const setRoomsForSite = (values: RegisterFormType) => {
    const site = values.siteName
      ? siteMeta?.sites.find(site => site.name === values.siteName)
      : undefined;

    if (!site || !site.rooms.length) {
      roomsForSite = [];
      return;
    }

    roomsForSite = site.rooms.map(room => ({
      text: room.name,
      value: room.name
    }));
  };

  return (
    <Formik<RegisterFormType>
      name="RegisterDevice"
      initialValues={initialStateWithParams}
      validationSchema={RegisterFormSchema}
      validate={setRoomsForSite}
      onSubmit={onSubmit}
      validateOnChange
      enableReinitialize
    >
      {({ isSubmitting, isValid, setValues, setFieldValue, values }) => (
        <div className="grid register-device-container">
          <div className="col-12 col-lg-6 offset-lg-3 col-md-8 offset-md-2">
            <Form className="form">
              <Help />
              <SyncTenant />
              <SyncSiteAddress
                initialValues={initialState}
                siteNameKey="siteName"
                setSiteAddressDisabled={setSiteAddressDisabled}
              />

              <Group>
                <Input
                  label={t("DEVICE_PIN")}
                  name="registrationCode"
                  data-testid="registrationCode"
                  tabIndex={1}
                />
              </Group>
              <Group>
                <Tenants />
              </Group>
              <Group>
                <Input
                  label={t("DEVICE_NAME")}
                  name="deviceName"
                  tabIndex={2}
                />
              </Group>
              <p className="help pt-15">{t("DEVICE_LOCATION_DETAILS_HELP")}</p>
              <Group>
                <StandardFormSelect
                  label={t("SITE_NAME")}
                  name="siteName"
                  options={sites}
                  tabIndex={3}
                  customValue
                  search
                  selectOnInput
                />
              </Group>
              <Room
                label={t("ROOM_NAME")}
                name="roomName"
                options={roomsForSite}
                tabIndex={4}
              />
              <Group>
                <Input label={t("FLOOR_NAME")} name="floorName" tabIndex={5} />
              </Group>
              <Group>
                <Input
                  name="capacity"
                  label={t("CAPACITY_OPTIONAL")}
                  tabIndex={6}
                  type="number"
                  onKeyPress={positiveNumberFilter}
                  onChange={e => {
                    setFieldValue(
                      "capacity",
                      !e.target.value || Number(e.target.value) >= 0
                        ? e.target.value
                        : values.capacity
                    );
                  }}
                />
              </Group>
              <Group className="pb-3">
                <AddressInput
                  tabIndex={7}
                  placeholder
                  name="siteAddress.address1"
                  disabled={siteAddressDisabled}
                  placeholderLabel={
                    siteAddressDisabled ? t("UNKNOWN") : t("SITE_ADDRESS")
                  }
                  onSelect={address => {
                    const { geohash, formattedAddress } = address;
                    setValues({
                      ...values,
                      siteAddress: {
                        geohash: geohash || "",
                        address1: formattedAddress || ""
                      }
                    });
                  }}
                />
              </Group>
              <Group className="text-center mt-2">
                <SubmitButton
                  isSubmitting={isSubmitting}
                  disabled={!isValid}
                  className="btn btn-primary btn-wide"
                  testId="submit-register-device"
                  labelKey="REGISTER_DEVICE"
                  submittingLabelKey="REGISTERING"
                />
              </Group>
            </Form>
          </div>
        </div>
      )}
    </Formik>
  );
};
const SuccessNotification: React.FC<{ deviceId: string }> = ({ deviceId }) => {
  const { t } = useTranslation();
  return (
    <span>
      {t("DEVICE_REGISTRATION_SUCCESS_1")}
      <Link
        to={generatePath("/manage/inventory/details/:deviceId", { deviceId })}
      >
        {t("DEVICE_REGISTRATION_SUCCESS_2")}
      </Link>
      {t("DEVICE_REGISTRATION_SUCCESS_3")}
    </span>
  );
};
const SharedRegistration: React.FC<{
  onSubmit: FormikConfig<RegisterFormType>["onSubmit"];
}> = ({ onSubmit }) => {
  const [pin] = useQueryParamString("device-pin");
  const { t } = useTranslation();
  const { Content } = Layout;
  return (
    <Layout>
      <div className="register-device">
        <Content scrolling>
          <div className="grid">
            <div className="col-12 col-12-np col-xl-6 offset-xl-3 col-lg-8 offset-lg-2 col-sm-10 offset-sm-1">
              <Content.Title>{t("REGISTER_DEVICE")}</Content.Title>
            </div>
          </div>
          <Content.ScrollingList>
            <div className="grid">
              <div className="col-12 col-12-np col-xl-6 offset-xl-3 col-lg-8 offset-lg-2 col-sm-10 offset-sm-1">
                <Card className="padded">
                  <RegisterDeviceForm
                    onSubmit={onSubmit}
                    initialPin={pin || ""}
                  />
                </Card>
              </div>
            </div>
          </Content.ScrollingList>
        </Content>
      </div>
    </Layout>
  );
};
export const RegisterDevice: React.FC = () => {
  const [, , clearPin] = useQueryParamString("device-pin");
  const [, sendRegistration] = useRegisterDeviceMutation();
  const [addNotification] = useNotification();
  const [showForm, setShowForm] = useState(true);
  const { t } = useTranslation();
  const MobileSuccess: React.FC = () => (
    <div className="register-success">
      <div className="info">
        <h1>{t("DEVICE_REGISTERED")}</h1>
        <p>{t("MOBILE_DIRECTIONS")}</p>
      </div>
      <button data-testid="register" onClick={() => setShowForm(true)}>
        {t("REGISTER_ANOTHER")}
      </button>
    </div>
  );

  const handleResponse = (
    registerResult: RegisterDeviceMutation["registerDevice"]
  ) => {
    if (registerResult.success) {
      if (!isMobile) {
        addNotification({
          type: "success",
          message: (
            <SuccessNotification deviceId={registerResult.deviceId || ""} />
          ),
          dismiss: DISMISS.DEFAULT
        });
      }

      return;
    }

    addNotification({
      type: "error",
      message: `${t("DEVICE_REGISTRATION_FAILED")}: ${registerResult.error}.`,
      dismiss: DISMISS.SLOW
    });
  };
  const onSubmit: FormikConfig<RegisterFormType>["onSubmit"] = async (
    form: RegisterFormType,
    { resetForm, setFieldValue }
  ) => {
    const { capacity, extras, ...tempDevice } = form;
    const device = {
      capacity: !capacity ? -1 : parseInt(capacity),
      ...tempDevice
    };
    const result = await sendRegistration(
      { device },
      {
        notify: { i18nKey: "TRY_AGAIN_WITH_ERROR", dismiss: DISMISS.USER_ONLY }
      }
    );

    if (result.data) {
      handleResponse(result.data.registerDevice);
      if (result.data.registerDevice.success) {
        resetForm();
      } else {
        setFieldValue("registrationCode", "");
      }
      clearPin();
      setShowForm(!isMobile);
    }
  };
  return showForm ? (
    <SharedRegistration onSubmit={onSubmit} />
  ) : (
    <MobileSuccess />
  );
};

export default RegisterDevice;
